import cuid from "cuid";
import {
  defaults,
  defaultsDeep,
  defaultTo,
  get,
  head,
  isEmpty,
  keyBy,
  map,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { GENERATE_SUBSCRIPTION_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import { BANK_ACCOUNT_OWNER_TYPE, KEYS, PAYMENT_TYPES } from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const BankInformation = ({
  project,
  onNext,
  onBack,
  updateUrl,
  currentStatus,
  tracer,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_MILTIS.BANK_INFORMATION", { returnObjects: true });
  // const [generateSubscriptionDocuments] = useMutation(
  //   GENERATE_SUBSCRIPTION_DOCUMENTS
  // );
  const [generateSubscriptionDocuments, { loading: generateLoading }] =
    useMutation(GENERATE_SUBSCRIPTION_DOCUMENTS);
  const [initialValues, setInitialValues] = useState(null);
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION, {
    refetchQueries: [
      {
        query: SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id: subscriptionId } },
      },
    ],
  });

  const initSubscription = useCallback(({ subscription: data }) => {
    const subscription = omitDeep(data, "__typename");

    const shouldBeAdherent =
      get(subscription, "payment.payer.owner.isAdherent", true) ||
      get(subscription, "insurancePlan.fields.payment.shouldBeAdherent", false);

    const initialValues = {
      // isPayerReceiver: true,
      isPayerReceiver: isEmpty(get(subscription, "payment.receiver.id"))
        ? true
        : get(subscription, "payment.receiver.id") ===
          get(subscription, "payment.payer.id"),
      telecoms: {
        receiver: defaults(
          keyBy(
            get(subscription, "payment.receiver.owner.telecoms", []),
            "system",
          ),
        ),
      },
      payment: {
        id: cuid(),
        type:
          head(get(subscription, "insurancePlan.fields.payment.type")) ||
          PAYMENT_TYPES.DEBIT,
        contact: get(subscription, "project.contact"),
        ...subscription.payment,
        debitDay:
          get(subscription, "insurancePlan.fields.payment.debitDay") || "5",
        payer: {
          id: cuid(),
          IBAN: null,
          BIC: null,
          ...subscription.payment?.payer,
          owner: {
            id: cuid(),
            ...subscription.payment?.payer?.owner,
            firstName: defaultTo(
              subscription.payment?.payer?.owner?.firstName,
              shouldBeAdherent
                ? get(subscription, "project.contact.user.firstname")
                : null,
            ),
            lastName: defaultTo(
              subscription.payment?.payer?.owner?.lastName,
              shouldBeAdherent
                ? get(subscription, "project.contact.user.lastname")
                : null,
            ),
            isAdherent: defaultTo(
              subscription.payment?.payer?.owner?.isAdherent,
              shouldBeAdherent,
            ),
            type: get(subscription, "insurancePlan.fields.tns")
              ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
              : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
          },
        },
        receiver: defaultsDeep(subscription.payment?.receiver, {
          id: cuid(),
          IBAN: null,
          BIC: null,
          owner: {
            id: cuid(),
            isAdherent: false,
            gender: null,
          },
        }),
      },
    };

    setInitialValues(initialValues);
  }, []);

  const { data: subscription, loading } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
    onCompleted: initSubscription,
  });

  const data = omitDeep(subscription, "__typename");

  // Check if names match adherent names (case insensitive)
  const checkIfNamesMatchAdherent = (firstName, lastName) => {
    const adherentFirstName = get(project, "contact.user.firstname", "").trim();
    const adherentLastName = get(project, "contact.user.lastname", "").trim();

    const ownerFirstName = (firstName || "").trim();
    const ownerLastName = (lastName || "").trim();

    const firstNameMatch =
      ownerFirstName.toLowerCase() === adherentFirstName.toLowerCase();
    const lastNameMatch =
      ownerLastName.toLowerCase() === adherentLastName.toLowerCase();

    return firstNameMatch && lastNameMatch;
  };

  const onSubmit = (values) => {
    // Update isAdherent for payer based on name comparison
    const payerFirstName = get(values, "payment.payer.owner.firstName", "");
    const payerLastName = get(values, "payment.payer.owner.lastName", "");
    const telecoms = get(values, "telecoms.receiver", []);
    const updatedTelecoms = map(telecoms, (item, key) =>
      defaults(
        { ...item, value: item?.value != null ? String(item.value) : null },
        { id: cuid(), system: key },
      ),
    );

    if (payerFirstName && payerLastName) {
      const payerIsAdherent = checkIfNamesMatchAdherent(
        payerFirstName,
        payerLastName,
      );
      values.payment.payer.owner.isAdherent = payerIsAdherent;
    }

    // Update isAdherent for receiver based on name comparison (only if receiver is different from payer)
    if (!values.isPayerReceiver) {
      const receiverFirstName = get(
        values,
        "payment.receiver.owner.firstName",
        "",
      );
      const receiverLastName = get(
        values,
        "payment.receiver.owner.lastName",
        "",
      );
      values.payment.receiver.owner.telecoms = updatedTelecoms;
      if (receiverFirstName && receiverLastName) {
        const receiverIsAdherent = checkIfNamesMatchAdherent(
          receiverFirstName,
          receiverLastName,
        );
        values.payment.receiver.owner.isAdherent = receiverIsAdherent;
      }
    } else {
      // If receiver is same as payer, use payer's isAdherent value
      values.payment.receiver.owner.isAdherent = get(
        values,
        "payment.payer.owner.isAdherent",
        false,
      );
    }

    const payment = omitDeep(values.payment, ["__typename", "contact"]);

    updateSubscription({
      variables: {
        where: {
          id: subscriptionId,
        },
        data: {
          payment: omitDeep(payment, "__typename"),
        },
      },

      onCompleted: (_) => {
        const source = localStorage.getItem(KEYS.SOURCE);
        updateUrl({
          subscriptionId,
          url: get(
            data,
            "subscription.insurancePlan.config.generatedUrl",
            `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
          ),
        });

        onNext({
          payload: {},
        });
      },
    });
  };

  if (loading || isEmpty(initialValues)) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
        showProfile
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
      />
      <View
        subscription={omitDeep(get(data, "subscription", {}), "__typename")}
        // subscription={{ ...omitDeep(data.subscription, "__typename"), project }}
        project={project}
        onNext={onSubmit}
        onBack={onBack}
        initialValues={initialValues}
        loading={generateLoading}
        translate={TRANSLATION}
      />
    </>
  );
};

export default BankInformation;
